import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Loader2, Search, Paperclip, Inbox } from 'lucide-react';
import { abrirArquivoComToken } from '../../../utils/arquivoComToken';

// =====================================================================
// FINANCEIRO — Mapa de Caixa: a CONSULTA (a rede, para o escritório).
//
// Lista os mapas lançados num período, já com a Quebra de Caixa e a Diferença
// GPRS que o servidor derivou. O recorte por loja é do backend: o gerente só vê
// a própria loja mesmo que forje o filtro.
// =====================================================================

const API = '/api/financeiro/mapa-caixa';
const COR = '#0d7a74';
const COR_FORTE = '#115e59';
const COR_FUNDO = '#f0fdfa';
const COR_BORDA = '#99f6e4';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });
const brl = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
const dataBr = (iso) => (iso ? String(iso).split('-').reverse().join('/') : '—');
const hojeIso = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const primeiroDoMes = () => `${hojeIso().slice(0, 7)}-01`;

// Célula de valor com sinal colorido (Quebra / Diferença GPRS).
const celSinal = (v) => {
  if (v === null || v === undefined) return <span style={{ color: '#94a3b8' }}>—</span>;
  const cor = v > 0 ? '#047857' : v < 0 ? '#b91c1c' : '#334155';
  return <span style={{ color: cor, fontWeight: 700 }}>{v > 0 ? '+' : ''}{brl(v)}</span>;
};

const th = { textAlign: 'right', padding: '10px 12px', fontSize: 12, fontWeight: 800, color: COR_FORTE, whiteSpace: 'nowrap', borderBottom: `2px solid ${COR_BORDA}` };
const thL = { ...th, textAlign: 'left' };
const td = { textAlign: 'right', padding: '9px 12px', fontSize: 13, color: '#0f172a', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', borderBottom: '1px solid #f1f5f9' };
const tdL = { ...td, textAlign: 'left' };

const MapaCaixaConsulta = () => {
  const [de, setDe] = useState(primeiroDoMes());
  const [ate, setAte] = useState(hojeIso());
  const [loja, setLoja] = useState('');
  const [lojas, setLojas] = useState([]);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/lojas`, { headers: auth() });
        if (r.ok) setLojas(await r.json());
      } catch { /* o filtro de loja é opcional; sem a lista, consulta a rede toda */ }
    })();
  }, []);

  const buscar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const params = new URLSearchParams({ de, ate });
      if (loja) params.set('loja', loja);
      const r = await fetch(`${API}/consulta?${params.toString()}`, { headers: auth() });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(corpo.message || 'Não foi possível carregar a consulta.');
      setDados(corpo);
    } catch (ex) {
      setErro(ex.message);
      setDados(null);
    } finally {
      setCarregando(false);
    }
  }, [de, ate, loja]);

  // Busca uma vez ao abrir (o período padrão é o mês corrente). Depois, só no botão.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { buscar(); }, []);

  const estiloInput = { padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, color: '#0f172a', background: '#fff', fontFamily: 'inherit' };

  return (
    <div>
      {/* Filtros */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 20 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>De</label>
          <input type="date" value={de} max={ate} onChange={(e) => setDe(e.target.value)} style={estiloInput} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Até</label>
          <input type="date" value={ate} min={de} onChange={(e) => setAte(e.target.value)} style={estiloInput} />
        </div>
        {lojas.length > 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Loja</label>
            <select value={loja} onChange={(e) => setLoja(e.target.value)} style={estiloInput}>
              <option value="">Todas</option>
              {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        )}
        <button onClick={buscar} disabled={carregando} style={{
          display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 18px',
          background: carregando ? '#cbd5e1' : COR, color: '#fff', border: 'none',
          borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: carregando ? 'default' : 'pointer',
        }}>
          {carregando ? <Loader2 size={16} className="spin" /> : <Search size={16} />} Buscar
        </button>
      </div>

      {erro && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 14, marginBottom: 16, background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontSize: 14 }}>
          <AlertTriangle size={18} /> <span>{erro}</span>
        </div>
      )}

      {dados?.truncado && (
        <div style={{ padding: 12, marginBottom: 12, background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a', borderRadius: 8, fontSize: 13 }}>
          Muitos registros no período — mostrando os 2.000 mais recentes. Estreite o intervalo para ver o resto.
        </div>
      )}

      {carregando && !dados ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#64748b', padding: 24 }}>
          <Loader2 size={18} className="spin" /> Carregando…
        </div>
      ) : !dados?.linhas?.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, color: '#94a3b8', padding: 40 }}>
          <Inbox size={36} /> <span>Nenhum mapa lançado no período.</span>
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 12 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 980 }}>
            <thead style={{ background: COR_FUNDO }}>
              <tr>
                <th style={thL}>Data</th>
                <th style={thL}>Loja</th>
                <th style={th}>Dinheiro Fita</th>
                <th style={th}>Depositado</th>
                <th style={th}>Nº Bananas</th>
                <th style={th}>Quebra</th>
                <th style={th}>GPRS Máquina</th>
                <th style={th}>Dif. GPRS</th>
                <th style={th}>Erros Pós</th>
                <th style={th}>Nulos Pós</th>
                <th style={th}>% Venda</th>
                <th style={thL}>Anexo</th>
              </tr>
            </thead>
            <tbody>
              {dados.linhas.map((l, i) => (
                <tr key={`${l.loja}-${l.data}-${i}`}>
                  <td style={tdL}>{dataBr(l.data)}</td>
                  <td style={tdL}><strong>{l.loja}</strong></td>
                  <td style={td}>{brl(l.totalDinheiroFita)}</td>
                  <td style={td}>{brl(l.totalValorBananas)}</td>
                  <td style={td}>{l.qtdBananas ?? '—'}</td>
                  <td style={td}>{celSinal(l.quebraCaixa)}</td>
                  <td style={td}>{brl(l.gprsMaquina)}</td>
                  <td style={td}>{celSinal(l.diferencaGprs)}</td>
                  <td style={td}>{brl(l.errosPosTotal)}</td>
                  <td style={td}>{brl(l.nulosPosTotal)}</td>
                  <td style={td}>{l.pctReferenteVenda === null || l.pctReferenteVenda === undefined ? '—' : `${l.pctReferenteVenda}%`}</td>
                  <td style={tdL}>
                    {l.anexoUrl ? (
                      <button onClick={() => abrirArquivoComToken(`${API}/anexo?url=${encodeURIComponent(l.anexoUrl)}`, `mapa-${l.loja}-${l.data}`)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', background: COR_FUNDO, color: COR_FORTE, border: `1px solid ${COR_BORDA}`, borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                        <Paperclip size={13} /> Ver
                      </button>
                    ) : <span style={{ color: '#94a3b8' }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MapaCaixaConsulta;
